/**
 * The deployment's auth configuration, held where non-React code can reach it.
 *
 * The same shape of problem as `token.ts` and solved the same way: the OIDC helpers are
 * called from places that have no hook and no props, so the value is published once at
 * boot and read from module state afterwards. It is written before anything renders,
 * which is what makes reading it synchronously safe.
 *
 * There are two values rather than one, because there are now two providers. The config
 * is every provider this backend accepts; the active one is the single provider this
 * browser is signing in against, chosen before `OidcProvider` mounts and fixed for the
 * life of the page. Everything downstream of the gate reads the active one and never has
 * to think about the list.
 */

import type { AuthConfig, AuthProviderConfig } from '../types/api';

let current: AuthConfig | null = null;
let active: AuthProviderConfig | null = null;

export function setAuthConfig(config: AuthConfig): void {
  current = config;
}

export function getAuthConfig(): AuthConfig | null {
  return current;
}

/** Record which provider this page is signing in against. Called once, before render. */
export function setActiveProvider(provider: AuthProviderConfig | null): void {
  active = provider;
}

export function getActiveProvider(): AuthProviderConfig | null {
  return active;
}

/** Whether this deployment expects the SPA to sign people in itself. */
export function requiresSignIn(config: AuthConfig): boolean {
  return usableProviders(config).length > 0;
}

/**
 * The providers complete enough to sign in against.
 *
 * A provider missing its authority or client id cannot produce a login, and offering it
 * gives a button that fails after the click rather than one that is not there. The
 * backend only reports configured providers, so this filter should never remove
 * anything; it exists so that a half-set environment degrades to the other provider
 * instead of to a broken screen.
 */
export function usableProviders(config: AuthConfig): AuthProviderConfig[] {
  return (config.providers ?? []).filter((p) => !!p.authority && !!p.client_id);
}
